import { BriefClosureStatus, IBrief } from "@shared/lib/types/brief";
import { CircularProgress, Input, Table, TableTitleLayout, Typography } from "@shared/ui";
import { Flex } from "antd";
import { ColumnsType } from "antd/es/table";
import { Search } from "lucide-react";
import { CLOSURE_TABS } from "../lib/constants";
import styles from "../styles.module.scss";

const { Title, Text } = Typography;

type ClosuresBriefsSectionProps = {
  isLoading: boolean;
  closureStatus: BriefClosureStatus;
  onClosureStatusChange: (status: BriefClosureStatus) => void;
  getTabCount: (status: BriefClosureStatus) => number | null;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  columns: ColumnsType<IBrief>;
  briefs: IBrief[];
  isFetching: boolean;
  pageNo: number;
  pageSize: number;
  totaldata: number;
  onPaginationChange: (page: number, size: number) => void;
  debouncedSearchQuery: string;
};

export function ClosuresBriefsSection({
  isLoading,
  closureStatus,
  onClosureStatusChange,
  getTabCount,
  searchQuery,
  onSearchQueryChange,
  columns,
  briefs,
  isFetching,
  pageNo,
  pageSize,
  totaldata,
  onPaginationChange,
  debouncedSearchQuery,
}: ClosuresBriefsSectionProps) {
  const activeTabMeta = CLOSURE_TABS.find((tab) => tab.value === closureStatus);

  return (
    <>
      <CircularProgress spinning={isLoading}>
        <section className={styles.contentSection}>
          <div className={styles.tableCard}> 
            <div
              className={`${styles.tableCardHeader} ${
                closureStatus === BriefClosureStatus.Closed
                  ? styles.tableCardHeaderMuted
                  : ""
              }`}
            >
              <Title level={5} className={styles.tableCardTitle}>
                {activeTabMeta?.title}
              </Title>
              <Text type="secondary" className={styles.tableCardDescription}>
                {activeTabMeta?.description}
              </Text>
            </div>

            <Table
              rowKey="_id"
              columns={columns}
              title={() => <TableTitleLayout 
                filterComponent={<Flex style={{width: "100%"}} gap={10}>
                  <Input
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder="Brief name, project, creator handle, or contact number…"
          allowClear
          prefix={<Search size={14}/>}
          style={{width: 300}}
        />
        <div
            className={styles.tabList}
            role="tablist"
            aria-label="Brief list view"
          >
            {CLOSURE_TABS.map((tab) => {
              const isActive = closureStatus === tab.value;
              const tabCount = getTabCount(tab.value);

              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""}`}
                  onClick={() => onClosureStatusChange(tab.value)}
                >
                  {tab.label}
                  {tabCount != null ? (
                    <span className={`${styles.tabCount} ${tab.countClass}`}>
                      {tabCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
                </Flex>}
              />}
              dataSource={briefs}
              loading={isFetching}
              className={styles.briefsTable}
              scroll={{ x: 800 }}
              pagination={{
                current: pageNo,
                pageSize,
                total: totaldata,
                onChange: onPaginationChange,
              }}
              locale={{
                emptyText: debouncedSearchQuery.trim()
                  ? `No briefs match "${debouncedSearchQuery.trim()}" in this view.`
                  : (activeTabMeta?.empty ?? "No closures found"),
              }}
            />
            </div>
        </section>
      </CircularProgress>
    </>
  );
}
